import { getWriteEpoch } from '@/Network/Requests/writeEpoch'

/** TTL dos caches de listas de select (escalas, unidades) — spec dashboard-mobile-v2 §4.2. */
export const SHARED_LOAD_TTL_MS = 5 * 60 * 1000

/**
 * Escopo do dado: o que o backend usa para decidir o que o usuário vê (token e
 * header `company_ids`, ver `Network/Requests/api.ts`) mais a época de escrita.
 * Trocar de empresa, de sessão ou escrever qualquer coisa muda a chave e o cache cai.
 * Lê o localStorage direto (mesmas chaves de AuthService/CompanyService) para não
 * depender do singleton `Network`.
 */
export function sessionScopeKey(): string {
  let scope = ''
  try {
    scope = [
      localStorage.getItem('@ME:access_token'),
      localStorage.getItem('@ME:doctor_access_token'),
      localStorage.getItem('@ME:companies_id')
    ].join('|')
  } catch {
    // fallback intencional: sem storage o escopo fica vazio e só a época/TTL valem.
  }
  return `${scope}#${getWriteEpoch()}`
}

export type SharedLoadOptions = { force?: boolean }

export interface SharedLoader<T> {
  /**
   * Devolve o valor em cache (mesmo escopo e dentro do TTL), reaproveita a promessa
   * em voo do mesmo escopo, ou busca. Erro não é cacheado e é relançado.
   * Uma resposta que chega depois de outra mais nova devolve a mais nova, para o
   * chamador não gravar dado velho por cima.
   */
  load(fetcher: () => Promise<T>, options?: SharedLoadOptions): Promise<T>
  invalidate(): void
}

export function createSharedLoader<T>(ttlMs = SHARED_LOAD_TTL_MS): SharedLoader<T> {
  let entry: { key: string; loadedAt: number; value: T; seq: number } | null = null
  let pending: { key: string; promise: Promise<T>; seq: number } | null = null
  let lastSeq = 0

  function load(fetcher: () => Promise<T>, { force = false }: SharedLoadOptions = {}): Promise<T> {
    const key = sessionScopeKey()
    if (!force && entry && entry.key === key && Date.now() - entry.loadedAt < ttlMs) {
      return Promise.resolve(entry.value)
    }
    if (!force && pending && pending.key === key) return pending.promise

    const seq = ++lastSeq
    const promise = (async (): Promise<T> => {
      try {
        const value = await fetcher()
        if (seq === lastSeq) {
          // Escrita/troca de escopo durante a carga: `key` já não é a atual e o
          // valor não será servido do cache na próxima chamada.
          entry = { key, loadedAt: Date.now(), value, seq }
          return value
        }
        // Superada por uma carga mais nova: converge para ela.
        if (pending && pending.seq > seq) return pending.promise
        if (entry && entry.seq > seq) return entry.value
        return value
      } finally {
        if (pending?.seq === seq) pending = null
      }
    })()
    pending = { key, promise, seq }
    return promise
  }

  function invalidate(): void {
    entry = null
    pending = null
    lastSeq += 1
  }

  return { load, invalidate }
}

/**
 * Um loader por instância de store (cada `createPinia()` — teste ou app — tem o seu).
 * Stores options-API não guardam Promise no state, por isso o WeakMap.
 */
export function sharedLoaderFor<T>(registry: WeakMap<object, SharedLoader<T>>, owner: object) {
  let loader = registry.get(owner)
  if (!loader) {
    loader = createSharedLoader<T>()
    registry.set(owner, loader)
  }
  return loader
}
